import React, { useEffect, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Empty, Skeleton, Space, Typography } from 'antd';
import store, {
  ICommitter,
  IContributor,
  IContributorUpdate,
} from '../store/Contributors';
import CommiterActions from './CommiterActions';
import FeaturedTable, { FeaturedTableColumnType } from './FeaturedTable';
import { ImportOutlined } from '@ant-design/icons';
import '../styles/styles.module.scss';
import {
  SelectContributorModal,
  selectContributorModalStore,
} from './SelectContributorModal';
import globalStore from 'ts/store/Global';
import { GroupType } from 'ts/constants/constants';

const GROUPS = [
  GroupType.UNSORTED,
  GroupType.EMPLOYEE,
  GroupType.EXTERNAL,
  GroupType.IGNORE,
];
const GROUPS_INLINE_EDIT = GROUPS.map((item) => ({ value: item }));
const GROUP_FILTER = GROUPS.map((item) => ({ text: item, value: item }));

const ContributorsList = ({ searchValue }: { searchValue: string }) => {
  const TOGGL_USER_OPTIONS = useMemo(() => {
    const re = /(@\S+)/;
    return store.togglUserList
      .map((user) => {
        const name = user?.name ?? '';
        const match = name.match(re);
        const sortKey = match?.[1]
          ? match[1].replace(/^@/, '')
          : name || '\uffff';
        return {
          label: match?.[1] ?? (name || 'no user'),
          value: user.id,
          fullLabel: name,
          sortKey,
        };
      })
      .sort((a, b) => {
        return a.sortKey.localeCompare(b.sortKey, 'en', {
          sensitivity: 'base',
        });
      });
  }, [store.togglUserList]);
  const navigate = useNavigate();

  useEffect(() => {
    store.setContributorsList();
    store.setTogglUserList();
  }, []);

  const handleSave = async (
    row: IContributor,
    changedValues: IContributorUpdate
  ) => {
    await store.editContributor(row.id, changedValues);
    await store.setContributorsList();
  };

  const handleMerge = async (
    action: string,
    source: IContributor | ICommitter,
    destinationId: string
  ) => {
    if (!source || !destinationId) {
      return;
    }
    switch (action) {
      case 'merge':
        await store.mergeContributors(source.id as string, destinationId);
        break;
      case 'attach':
        await store.attachCommiterToContributor(
          source.id as number,
          destinationId
        );
        break;
    }

    await store.setContributorsList();
  };

  // only non-unsorted
  const selectOptions = store.contributorsList
    .filter(
      (contributor) =>
        contributor.group === GroupType.EMPLOYEE ||
        contributor.group === GroupType.EXTERNAL
    )
    .map((contributor) => ({
      label: `${contributor.name}(${contributor.committers
        .map((commiter) => `${commiter.author}<${commiter.email}>`)
        .join(', ')})`,
      value: contributor.id,
    }));

  const getContributorsSelectOptions = (selected: String) => {
    return selectOptions.filter((option) => option.value !== selected);
  };

  const columns: FeaturedTableColumnType<IContributor>[] = [
    {
      key: 'actions',
      width: '5%',
      render: (_, record) => (
        <Space direction="horizontal">
          <Button
            type="text"
            icon={<ImportOutlined style={{ fontSize: '18px' }} />}
            title="Merge"
            style={{ padding: 0, height: 'auto' }}
            onClick={(e) => {
              e.stopPropagation();
              selectContributorModalStore.show({
                action: 'merge',
                source: record,
                options: getContributorsSelectOptions(record.id),
                onOk: handleMerge,
              });
            }}
          ></Button>
        </Space>
      ),
    },
    {
      key: 'name',
      title: 'Name',
      dataIndex: 'name',
      width: '20%',
      editable: true,
      render: (text: String, record) => {
        if (
          record.group === GroupType.EMPLOYEE ||
          record.group === GroupType.EXTERNAL
        ) {
          return (
            <a
              onClick={(e: React.MouseEvent<HTMLElement, MouseEvent>) => {
                e.stopPropagation();
                globalStore.setContributorId(record.id);
                navigate('/contributors/by-user');
              }}
            >
              {text}
            </a>
          );
        } else {
          return <Typography.Text strong>{text}</Typography.Text>;
        }
      },
    },
    {
      key: 'committers',
      title: 'Committers',
      dataIndex: 'committers',
      width: '30%',
      render: (committers: ICommitter[], record) => {
        return committers.map((committer: ICommitter) => (
          <CommiterActions
            key={committer.id}
            commiter={committer}
            handleAttach={() => {
              selectContributorModalStore.show({
                action: 'attach',
                source: committer,
                options: getContributorsSelectOptions(record.id),
                onOk: handleMerge,
              });
            }}
            handleDetach={async () => {
              await store.detachCommiter(committer.id);
              await store.setContributorsList();
            }}
          />
        ));
      },
    },
    {
      key: 'toggl_id',
      title: 'Toggl user',
      dataIndex: 'toggl_id',
      width: '10%',
      inputType: 'select',
      editable: true,
      showSearch: true,
      editOptions: TOGGL_USER_OPTIONS,
      render: (value) => {
        const option = TOGGL_USER_OPTIONS.find((opt) => opt.value === value);
        let display = option?.label;
        const match = display?.match(/(@\S+)/);
        if (match) {
          display = match[0] ?? 'no user';
        }
        return (
          <Typography.Text strong style={{ color: display ? 'black' : 'red' }}>
            {display ?? 'No User'}
          </Typography.Text>
        );
      },
    },
    {
      key: 'group',
      title: 'Group',
      dataIndex: 'group',
      width: '10%',
      editable: true,
      inputType: 'select',
      editOptions: GROUPS_INLINE_EDIT,
      filters: GROUP_FILTER,
      defaultFilteredValue: [GroupType.UNSORTED, GroupType.EMPLOYEE],
      onFilter: (value, record) => record.group === value,
      render: (value: string) => {
        let color = 'black';
        switch (value) {
          case GroupType.UNSORTED:
            color = 'red';
            break;
          case GroupType.EMPLOYEE:
            color = 'green';
            break;
          case GroupType.EXTERNAL:
            color = 'blue';
            break;
          case GroupType.IGNORE:
            color = 'orange';
            break;
        }
        return <span style={{ color }}>{value}</span>;
      },
    },
    {
      key: 'addedOn',
      title: 'Added On',
      dataIndex: 'added_on',
      width: '10%',
    },
  ];

  // search inside contributor name, committer author & email
  const records = store.contributorsList.filter(
    (contributor) =>
      contributor.name.toLowerCase().includes(searchValue) ||
      contributor.committers.some(
        (committer) =>
          committer.author.toLowerCase().includes(searchValue) ||
          committer.email.toLowerCase().includes(searchValue)
      )
  );

  if (store.togglUserList.length === 0) return <Skeleton />;

  return (
    <Card
      style={{ height: '100%', marginBottom: 0 }}
      styles={{
        body: {
          padding: '8px',
          backgroundColor: '#eeeeee',
        },
      }}
    >
      {records.length === 0 ? (
        <Empty description="No matches!" />
      ) : (
        <>
          <FeaturedTable<IContributor>
            bordered
            pagination={false}
            rowKey={'id'}
            dataSource={records}
            columns={columns}
            scroll={{
              y: 'calc(100vh - var(--header-h) - var(--sub-header-h) - 130px)',
            }}
            handleSave={handleSave}
          />
        </>
      )}
      <SelectContributorModal />
    </Card>
  );
};

export default observer(ContributorsList);
